import * as v from 'valibot'

/**
 * FDA SPL XML Parser
 *
 * Parses FDA Structured Product Labeling (SPL) XML files and extracts
 * sections as clean, readable text optimized for LLM processing.
 */

// FDA SPL LOINC codes for key sections
export const FDA_SECTION_CODES = {
  INDICATIONS_AND_USAGE: '34067-9',
  DOSAGE_AND_ADMINISTRATION: '34068-7',
  CLINICAL_STUDIES: '34092-7',
  CONTRAINDICATIONS: '34070-3',
  WARNINGS_AND_PRECAUTIONS: '43685-7',
  ADVERSE_REACTIONS: '34084-4',
  DRUG_INTERACTIONS: '34073-7',
  DESCRIPTION: '34089-3',
  CLINICAL_PHARMACOLOGY: '34090-1',
  HOW_SUPPLIED: '34069-5',
} as const

export type FdaSectionCode = keyof typeof FDA_SECTION_CODES

export interface FdaSection {
  loincCode: string
  displayName: string
  text: string
  charCount: number
  estimatedTokens: number
}

export interface FdaDrugInfo {
  brandName: string
  genericName: string | null
  formulation: string | null
  effectiveTime: string | null
  manufacturer: string | null
  approvalYear: string | null
  setId: string | null // FDA SPL setId UUID for DailyMed linking
  ndcCodes: string[] // NDC code from FDA label (e.g., "50242-150-01")
}

export const fdaXmlParseResultSchema = v.object({
  drugInfo: v.object({
    brandName: v.string(),
    genericName: v.nullable(v.string()),
    formulation: v.nullable(v.string()),
    effectiveTime: v.nullable(v.string()),
    manufacturer: v.nullable(v.string()),
    approvalYear: v.nullable(v.string()),
    setId: v.nullable(v.string()),
    ndcCodes: v.array(v.string()),
  }),
  sections: v.record(
    v.string(),
    v.object({
      loincCode: v.string(),
      displayName: v.string(),
      text: v.string(),
      charCount: v.number(),
      estimatedTokens: v.number(),
    }),
  ),
  rawXmlLength: v.number(),
  totalExtractedChars: v.number(),
  totalEstimatedTokens: v.number(),
})

export type FdaXmlParseResult = v.InferOutput<typeof fdaXmlParseResultSchema>

/**
 * Extract text content from XML, preserving structure for readability.
 */
function extractTextFromXml(xml: string): string {
  let text = xml

  // Remove XML declaration and processing instructions
  text = text.replace(/<\?[^?]*\?>/g, '')

  // Convert common structural elements to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n')
  text = text.replace(/<\/paragraph>/gi, '\n')
  text = text.replace(/<\/item>/gi, '\n')
  text = text.replace(/<\/tr>/gi, '\n')
  text = text.replace(/<\/thead>/gi, '\n')
  text = text.replace(/<\/tbody>/gi, '\n')

  // Convert table cells to tab-separated for structure
  text = text.replace(/<\/td>/gi, '\t')
  text = text.replace(/<\/th>/gi, '\t')

  // Convert list items to bullet points
  text = text.replace(/<item[^>]*>/gi, '• ')

  // Remove all remaining XML tags
  text = text.replace(/<[^>]+>/g, '')

  // Decode HTML entities
  text = text.replace(/&amp;/g, '&')
  text = text.replace(/&lt;/g, '<')
  text = text.replace(/&gt;/g, '>')
  text = text.replace(/&quot;/g, '"')
  text = text.replace(/&apos;/g, "'")
  text = text.replace(/&#xA0;/gi, ' ') // Non-breaking space
  text = text.replace(/&#x[0-9A-Fa-f]+;/g, '') // Remove other hex entities
  text = text.replace(/&#[0-9]+;/g, '') // Remove decimal entities

  // Clean up whitespace
  text = text.replace(/[ \t]+/g, ' ') // Collapse horizontal whitespace
  text = text.replace(/\n[ \t]+/g, '\n') // Remove leading whitespace on lines
  text = text.replace(/[ \t]+\n/g, '\n') // Remove trailing whitespace on lines
  text = text.replace(/\n{3,}/g, '\n\n') // Collapse multiple newlines to max 2
  text = text.trim()

  return text
}

/**
 * Find a section by LOINC code and extract its content.
 */
function findSectionByCode(xml: string, loincCode: string): FdaSection | null {
  // Pattern to find the code element with the target LOINC code
  const codePattern = new RegExp(
    `<code[^>]*code="${loincCode}"[^>]*displayName="([^"]+)"[^>]*/?>`,
    'i',
  )

  const codeMatch = xml.match(codePattern)
  if (!codeMatch?.[1]) {
    return null
  }

  const displayName = codeMatch[1].replace(/&amp;/g, '&')
  const codeIndex = xml.indexOf(codeMatch[0])

  // Find the containing <component> start before this code
  let componentStart = xml.lastIndexOf('<component>', codeIndex)
  if (componentStart === -1) {
    componentStart = xml.lastIndexOf('<component ', codeIndex)
  }

  if (componentStart === -1) {
    return null
  }

  // Find the matching </component> end, handling nesting
  let depth = 1
  let searchStart = xml.indexOf('>', componentStart) + 1
  let componentEnd = -1

  while (depth > 0 && searchStart < xml.length) {
    const nextOpen = xml.indexOf('<component', searchStart)
    const nextClose = xml.indexOf('</component>', searchStart)

    if (nextClose === -1) break

    if (nextOpen !== -1 && nextOpen < nextClose) {
      depth++
      searchStart = nextOpen + 10
    } else {
      depth--
      if (depth === 0) {
        componentEnd = nextClose + '</component>'.length
      } else {
        searchStart = nextClose + '</component>'.length
      }
    }
  }

  if (componentEnd === -1) {
    return null
  }

  const sectionXml = xml.slice(componentStart, componentEnd)
  const text = extractTextFromXml(sectionXml)
  const formattedText = text?.replace(/^[\d]+\s+[^\n]+\n\n/, '')

  return {
    loincCode,
    displayName,
    // Remove heading from section text
    text: formattedText,
    charCount: formattedText.length,
    estimatedTokens: Math.ceil(formattedText.length / 4),
  }
}

/**
 * Extract drug information from XML metadata.
 */
function extractDrugInfo(xml: string): FdaDrugInfo {
  let brandName = 'Unknown Drug'
  let genericName: string | null = null
  let manufacturer: string | null = null
  let approvalYear: string | null = null
  let formulation: string | null = null
  let effectiveTime: string | null = null

  // Extract from title element
  // Format: "OCREVUS® (ocrelizumab) injection, for intravenous use\nInitial U.S. Approval: 2017"
  const titleMatch = xml.match(/<title[^>]*>([\s\S]*?)<\/title>/i)
  if (titleMatch?.[1]) {
    const titleText = extractTextFromXml(titleMatch[1])

    // Try to extract brand name (usually first word, may have ® or ™)
    const brandMatch = titleText.match(/[A-Z]+(?:\s+[A-Z]+)*/g)
    if (brandMatch?.[1]) {
      brandName = brandMatch[1].replace(/[®™]/g, '').trim()
    }

    // Try to extract formulation (usually after parentheses)
    const formulationMatch = titleText.match(
      /\)\s+(.*?)(?:\s*<br\s*\/?>|\n|$)/i,
    )
    if (formulationMatch?.[1]) {
      formulation = `${formulationMatch[1].trim()}`
    }

    // Try to extract approval year
    const yearMatch = titleText.match(/Approval:\s*(\d{4})/i)
    if (yearMatch?.[1]) {
      approvalYear = yearMatch[1]
    }
  }

  const effectiveTimeMatch = xml.match(
    /<effectiveTime[^>]+?value="([^"]+)"[^>]*\/>/i,
  )
  if (effectiveTimeMatch?.[1]) {
    effectiveTime = effectiveTimeMatch[1]
  }

  // Try to extract generic name from genericMedicine
  const genericMatch = xml.match(
    /<genericMedicine>[\s\S]*?<name>(.*?)<\/name>[\s\S]*?<\/genericMedicine>/,
  )
  if (genericMatch?.[1]) {
    genericName = genericMatch[1].trim()
  }

  // Extract manufacturer from representedOrganization
  const orgMatch = xml.match(
    /<representedOrganization>[\s\S]*?<name>([^<]+)<\/name>/i,
  )
  if (orgMatch?.[1]) {
    manufacturer = orgMatch[1].trim()
  }

  // Extract setId from <setId root="UUID"/>
  let setId: string | null = null
  const setIdMatch = xml.match(/<setId[^>]*root="([^"]+)"[^>]*\/?>/i)
  if (setIdMatch?.[1]) {
    setId = setIdMatch[1]
  }

  // Extract ALL NDC codes from code elements with codeSystem 2.16.840.1.113883.6.69
  // Valid NDC formats per 21 CFR 207.33:
  //   - 4-4-2 (10 digits): 4-digit labeler, 4-digit product, 2-digit package
  //   - 5-3-2 (10 digits): 5-digit labeler, 3-digit product, 2-digit package
  //   - 5-4-1 (10 digits): 5-digit labeler, 4-digit product, 1-digit package
  //   - 6-3-2 (11 digits): 6-digit labeler, 3-digit product, 2-digit package
  //   - 6-4-1 (11 digits): 6-digit labeler, 4-digit product, 1-digit package
  // Drugs can have multiple NDC codes - store all as comma-separated values
  // Prefer 3-segment (full package-level) NDC codes, fall back to 2-segment
  let ndcCodes: string[] = []

  // Find all 3-segment NDC codes (package-level, most specific)
  // Regex: 4-6 digit labeler, 3-4 digit product, 1-2 digit package
  const ndc3SegmentRegex =
    /<code[^>]*code="(\d{4,6}-\d{3,4}-\d{1,2})"[^>]*codeSystem="2\.16\.840\.1\.113883\.6\.69"[^>]*\/?>/gi
  const ndc3SegmentMatches: string[] = []
  let match
  while ((match = ndc3SegmentRegex.exec(xml)) !== null) {
    if (!ndc3SegmentMatches.includes(match[1]!)) {
      ndc3SegmentMatches.push(match[1]!)
    }
  }

  if (ndc3SegmentMatches.length > 0) {
    ndcCodes = ndcCodes.concat(ndc3SegmentMatches)
  } else {
    // Fall back to 2-segment NDC codes (product-level, no package segment)
    // Regex: 4-6 digit labeler, 3-4 digit product
    const ndc2SegmentRegex =
      /<code[^>]*code="(\d{4,6}-\d{3,4})"[^>]*codeSystem="2\.16\.840\.1\.113883\.6\.69"[^>]*\/?>/gi
    const ndc2SegmentMatches: string[] = []
    while ((match = ndc2SegmentRegex.exec(xml)) !== null) {
      if (!ndc2SegmentMatches.includes(match[1]!)) {
        ndc2SegmentMatches.push(match[1]!)
      }
    }
    if (ndc2SegmentMatches.length > 0) {
      ndcCodes = ndcCodes.concat(ndc2SegmentMatches)
    }
  }

  return {
    brandName,
    genericName,
    effectiveTime,
    formulation,
    manufacturer,
    approvalYear,
    setId,
    ndcCodes,
  }
}

/**
 * Parse an FDA SPL XML file and extract all key sections.
 *
 * @param xmlContent - Raw XML content as string
 * @returns Parsed result with drug info and sections
 */
export function parseFdaXml(xmlContent: string): FdaXmlParseResult {
  const drugInfo = extractDrugInfo(xmlContent)

  const sections: Record<string, FdaSection> = {}
  let totalExtractedChars = 0

  // Extract each target section
  for (const [sectionName, loincCode] of Object.entries(FDA_SECTION_CODES)) {
    const section = findSectionByCode(xmlContent, loincCode)
    if (section) {
      sections[sectionName] = section
      totalExtractedChars += section.charCount
    }
  }

  return {
    drugInfo,
    sections,
    rawXmlLength: xmlContent.length,
    totalExtractedChars,
    totalEstimatedTokens: Math.ceil(totalExtractedChars / 4),
  }
}

/**
 * Extract a single section from FDA XML by section name.
 *
 * @param xmlContent - Raw XML content
 * @param sectionName - Section name (e.g., 'INDICATIONS_AND_USAGE')
 * @returns Section content or null if not found
 */
export function extractFdaSection(
  xmlContent: string,
  sectionName: FdaSectionCode,
): FdaSection | null {
  const loincCode = FDA_SECTION_CODES[sectionName]
  if (!loincCode) {
    return null
  }
  return findSectionByCode(xmlContent, loincCode)
}

/**
 * Extract drug name from FDA XML metadata.
 * This is a lightweight operation that doesn't require parsing the full document.
 *
 * @param xmlContent - Raw XML content
 * @returns Drug info with brand and generic names
 */
export function extractDrugNameFromXml(xmlContent: string): FdaDrugInfo {
  return extractDrugInfo(xmlContent)
}

/**
 * Extract the raw title text from FDA XML for LLM processing.
 * Preserves the structure but converts XML to readable text.
 *
 * @param xmlContent - Raw XML content
 * @returns Title text as string, or null if not found
 */
export function extractTitleText(xmlContent: string): string | null {
  const titleMatch = xmlContent.match(/<title[^>]*>([\s\S]*?)<\/title>/i)
  if (!titleMatch?.[1]) {
    return null
  }
  return extractTextFromXml(titleMatch[1])
}
